import React from 'react';

import { Row, Col, Container } from 'reactstrap';

import { FCard, FPropControl } from "../base/components/FilterUI";
import BrandingEditor from './BrandingEditor';
import Misc from '../base/components/Misc';
import { ColourWarning } from '../base/components/Colour';
import { Card } from '../base/components/CardAccordion';
import KWorkflow from './KWorkflow';
import EditVideosForm from './EditVideosForm';
import DataStore from '../base/plumbing/DataStore';
import AdWysiwyg from './AdWysiwyg';
import { KAdFormat } from '../base/data/Advert';

import { ExtendedBrandingControls as PlayerEBC } from './AdDesignControls';


// Needs to live outside of CTAItemEditor because redeclaring the function breaks identity on redraw & loses focus on every keystroke
function CTALabelControl({ path }) {
  return <FPropControl path={path} prop="label" label="label" help="Label for the big shiny do-this button" />;
}

function CTAItemEditor({ item, path, i, setError }) {
	// pop the extra paths bits to get the advert NB we dont have an elegant way to pass it in through ListEditor.
	const adpath = path.slice(0, -3);
	let advert = DataStore.getValue(adpath) || {};
	let paramControls = null;
	let ppath = path.concat('params');
	let typeHelp = null;

	// none, consent, connect, email, like, share, link, coupon, appinstall, custom

	switch (item.type) {
	case 'appinstall':
		paramControls = <>
			<FPropControl path={ppath} prop="apple_url" label="Apple App Store link" />
			<FPropControl path={ppath} prop="android_url" label="Android Play Store link" />
		</>;
		break;
	case 'button':
		paramControls = (
			<>
				<Row>
					<Col sm="6"><CTALabelControl path={path} /></Col>
					<Col sm="3"><FPropControl required label="Button colour" type="color" path={ppath} prop="backgroundColor" /></Col>
					<Col sm="3"><FPropControl label="Button text colour" type="color" path={ppath} prop="color" /></Col>
				</Row>
				<Row>
					<Col sm="12">
						<FPropControl label="Alternative link" help="New link for CTA button, defaults to vertisers url if not set" type="url" path={ppath} prop="altURL" />
					</Col>
				</Row>
			</>
		);
		break;
	case 'connect':
		typeHelp = 'Connect with Good-Loop via email or social media. Like the `email` CTA but with more options for the user.';
		paramControls = <CTALabelControl path={path} />;
		break;
	case 'email':
		typeHelp = 'Connect with Good-Loop via email (which builds our mailing-list / user-base).';
		paramControls = <CTALabelControl path={path} />;
		break;
	case 'consent':
		typeHelp = 'yes/no consent to track. Change the button labels in "Custom Text -> Advanced" with text IDs "yes" and "no".';
		break;
	case 'coupon':
		paramControls = <>
			<CTALabelControl path={path} />
			<FPropControl path={ppath} prop="couponcode" label="Coupon" />
		</>;
		break;
	case 'link':
		// NB: uses the advert-level url
		let placeholderUrl = advert.url ? advert.url : "Landing page URL unset!";
		paramControls = <>
			<CTALabelControl path={path} />
			<FPropControl placeholder={placeholderUrl} label="Link url" help="URL for CTA links, defaults to vertisers landing page url if not set" type="url" path={ppath} prop="altURL" />
		</>;
		typeHelp = "Link to the brand's landing page";
		break;
	case 'like':
		paramControls = <>
			Status: NOT READY
			<FPropControl path={ppath} prop="facebook" label="Facebook post" />
		</>;
		break;
		// case 'nomore': no longer needed, as we use the default as a prototype now (not a fill in)
		// 	typeHelp = 'No more CTAs - which means the adserver will not add any default ones';
		// 	break;
	} // ./paramControls

	// TODO connect, like, share
	const TypeControl = () => (
		<FPropControl label="Type" type="select"
			path={path} prop="type"
			options={'consent email connect link coupon appinstall custom button'.split(' ')}
			help={typeHelp}
		/>
	);

	// reduced editors:
	// ...no parameters for nomore
	if (item.type === 'nomore') {
		return <TypeControl />;
	}

	// Every CTA type gets these controls
	const commonControls = (
		<>
	<Row>
		<Col sm="8">
			<FPropControl required={item.type === 'custom'} label="Custom HTML" type="textarea" path={path} prop="html" />
			Advertiser's CTA to be shown along the charities?
		</Col>
	</Row>
	<Row className="pl-3">
		<FPropControl label="Show during video" type="checkbox" item={advert} path={path} prop="duringVideo" />
		<FPropControl label="Show on end card" dflt={true} type="checkbox" item={advert} path={path} prop="onEndCard" />
	</Row>
	</>
	);

	// ...the std-wrapper lead/label/help isnt shown for custom -- just the html
	if (item.type === 'custom') {
		return <>
			<TypeControl />
			{commonControls}
		</>;
	}

	// normal case
	return <>
		<TypeControl />
		<FPropControl optional path={path} prop="lead" label="Lead text / strapline" />
		{paramControls}
		<FPropControl optional path={path} prop="help" label="Help text" help="Displayed underneath the CTA" />
		{commonControls}
	</>;
}


function CTAEditor({ advert, path, ...other }) {
	let warning = advert.url ? null : 'You must set a landing page URL';
	return (
		<FCard><Card title="Target Links &amp; CTAs" warning={warning} {...other}>
			<Container>
				<Row>
					{(advert.format==='display' || advert.format==='banner') && <Col sm="6">
						<FPropControl label="Use proxy when embedding" type="checkbox" item={advert} path={path} prop="needsProxy"
							help={`If your landing page is set up to disable embedding in an iframe, tick this box. If you don't know what that means, it's safe to leave this alone.`}
						/>
					</Col>}
					<Col sm="6">
						<FPropControl label="Add Google Analytics tracking (UTM) codes to the landing page url"
							type="checkbox" item={advert} path={path} prop="addUTM"
							help="When serving the ad, this will add utm_source:Good-Loop, utm_medium:video, utm_campaign:the campaign name set here"
						/>
					</Col>
				</Row>
			</Container>

			<p>
				You can have multiple CTAs. All of them will be shown in order (so put the main one first)
				on the end-card, and on the campaign-impact page.	Some of them may be shown alongside the video advert.
			</p>
			<Misc.ListEditor path={path.concat(['variantSpec', 'ctas'])} ItemEditor={CTAItemEditor}
				createText="Add a CTA"
			/>
		</Card></FCard>
	);
}


function EndCardDesignControls({ advert, path }) {
	//  Social mode flow is "splash video on platform --> adunit main screen" rather than "adunit main screen --> adunit end card"
	const isSocial = KAdFormat.social === advert.format;

	return <>
		<AdWysiwyg advert={advert} adid={advert.id} path={path} skipToEndCard={!isSocial} />
		<div>
			<BrandingEditor entity={advert} path={path} PropControl={FPropControl}
				warning={advert.branding && advert.branding.logo ? null : 'not complete'}
				workflow={KWorkflow.DesignPostEngagement}
			>
				{/* In social mode we need to use the main-screen background etc controls */}
				{isSocial ? (
					<PlayerEBC path={[...path, 'branding']} />
				) : (
					<ExtendedBrandingControls path={path} />
				)}
			</BrandingEditor>

			<EditVideosForm advert={advert} path={path} endcard />

			<CTAEditor advert={advert} path={path} />
		</div>
	</>;
}


/**
 * Override background graphics and colours etc
 */
function ExtendedBrandingControls({ path }) {
	const bPath = [...path, 'branding'];
	const aPath = [...path, 'advanced'];

	let advSettings = DataStore.getValue(aPath);
	let isTrees = /^trees/.test(advSettings?.playerVariant);

	return <div>
		<h4>End-card branding</h4>
		<Row>
			<Col xs="12" sm="4" md="3"><FPropControl path={bPath} prop="endCardBgImage" label="End card background image" type="imgUpload" version="standard" cacheControls /></Col>
			<Col xs="12" sm="4" md="3"><FPropControl path={bPath} prop="endCardBgColor" label="End card background colour" type="color" /></Col>
			<Col xs="12" sm="4" md="3"><FPropControl path={aPath} prop="endCardLogo" label="Override logo" help="Set this to use a different variant of the brand's logo on the end card." type="imgUpload" version="standard" cacheControls /></Col>
			<Col xs="12" sm="4" md="3"><FPropControl path={bPath} prop="endCardColor" label="Override text colour" help="Set this to use a different text colour on the end card." type="color" /></Col>
			{/* not implemented into ad yet!*/ isTrees ? <Col xs="12" sm="4" md="3"><FPropControl path={bPath} prop="endCardTextBgColor" label="Set this to use a different background colour on TADG block-background text." type="color" /></Col> : null}
		</Row>
		<ColourWarning
			hex1={DataStore.getValue([...bPath, 'endCardBgColor']) || '#F8EDD2'}
			hex2={DataStore.getValue([...bPath, 'endCardColor']) || DataStore.getValue([...bPath, 'color']) || '#000000'}
			message="The end-card text may be unreadable. Make sure that there is enough contrast between the background and text colours selected."
		/>
		{/* not implemented into ad yet!*/ isTrees ? <ColourWarning
			hex1={DataStore.getValue([...bPath, 'endCardTextBgColor']) || '#F8EDD2'}
			hex2={DataStore.getValue([...bPath, 'endCardColor']) || DataStore.getValue([...bPath, 'color']) || '#000000'}
			message="The block-background text may be unreadable. Make sure that there is enough contrast between the background and text colours selected."
		/> : null}
	</div>;
}


export default EndCardDesignControls;
